import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { VrboService } from '@app/_services/vrbo.service';

@Component({
    selector: 'app-vrbo-rate-guest',
    templateUrl: './vrbo-rate-guest.component.html',
    styleUrls: ['./vrbo-rate-guest.component.css'],
    standalone: false
})
export class VRBORateGuestComponent implements OnInit {
  form: UntypedFormGroup;
  shortTermRentalId: number;
  reservationId: string;
  guestName: string;
  submitting = false;
  existingRateGuest: any = null;
  isEdit = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicDialogConfig: DynamicDialogConfig,
    private messageService: MessageService,
    private vrboService: VrboService
  ) {}

  ngOnInit(): void {
    this.shortTermRentalId = this.dynamicDialogConfig.data?.shortTermRentalId;
    this.reservationId = this.dynamicDialogConfig.data?.reservationId;
    this.guestName = this.dynamicDialogConfig.data?.guestName || '';
    this.buildForm();
    this.checkExistingRating();
  }

  buildForm(): void {
    this.form = this.fb.group({
      overallRating: [null, Validators.required],
      overallRatingDesc: [''],
      cleanlinessRating: [null],
      houseRulesRating: [null],
      communicationRating: [null],
      privateGuestMsg: [''],
      recommendGuest: [false]
    });
  }

  checkExistingRating(): void {
    if (!this.reservationId) return;

    const resId = parseInt(this.reservationId, 10);
    if (isNaN(resId)) return;

    this.vrboService.findRateGuestByReservationId(resId).subscribe(
      (res: any) => {
        const rateGuests = res?.data?.rateGuests || [];
        if (rateGuests.length > 0) {
          this.existingRateGuest = rateGuests[0];
          this.isEdit = true;
          this.populateForm(this.existingRateGuest);
        }
      },
      () => {} // Silently handle - no existing rating
    );
  }

  populateForm(rg: any): void {
    this.form.patchValue({
      overallRating: this.parseRating(rg.overallRating),
      overallRatingDesc: rg.overallRatingDesc || '',
      cleanlinessRating: this.parseRating(rg.cleanlinessRating),
      houseRulesRating: this.parseRating(rg.houseRulesRating),
      communicationRating: this.parseRating(rg.communicationRating),
      privateGuestMsg: rg.privateGuestMsg || '',
      recommendGuest: rg.recommendGuest || false
    });
  }

  parseRating(value: any): number | null {
    if (value == null) return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  }

  submit(): void {
    if (this.form.invalid) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Overall rating is required.' });
      return;
    }

    this.submitting = true;
    const formData = this.form.value;

    const payload: any = {
      overallRating: formData.overallRating?.toString(),
      overallRatingDesc: formData.overallRatingDesc,
      cleanlinessRating: formData.cleanlinessRating?.toString() || null,
      houseRulesRating: formData.houseRulesRating?.toString() || null,
      communicationRating: formData.communicationRating?.toString() || null,
      privateGuestMsg: formData.privateGuestMsg,
      recommendGuest: formData.recommendGuest,
      reviewSource: 2, // VRBO
      shortTermRentalId: this.shortTermRentalId
    };

    // Add reservationId if it's a valid number
    const resId = parseInt(this.reservationId, 10);
    if (!isNaN(resId)) {
      payload.reservationId = resId;
    }

    // If editing, include the existing ID
    if (this.isEdit && this.existingRateGuest?.rateGuestId) {
      payload.rateGuestId = this.existingRateGuest.rateGuestId;
    }

    this.vrboService.createRateGuest(payload).subscribe(
      (res: any) => {
        this.submitting = false;
        if (res?.statusCode === 200) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Guest rated successfully.' });
          this.dynamicDialogRef.close({ action: 'RATED' });
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res?.message || 'Failed to rate guest.' });
        }
      },
      (err: any) => {
        this.submitting = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || err?.message || 'Failed to rate guest.' });
      }
    );
  }

  cancel(): void {
    this.dynamicDialogRef.close();
  }
}
